import type * as Types from '../../types'
import { 
	isArray,
	isDefined, 
	isNumber, 
	isString, 
	roundToPrecision 
} from '../../utils'

const DEFAULT_PRECISION = 4
const MATH_2PI = Math.PI * 2
const UNIT_X: Types.Vector2D = [{ x: 0, y: 0 }, { x: 1, y: 0 }]

export function addPoints(
	point$a: Types.Point,
	point$b: Types.Point
): Types.Point {
	return {
		x: point$a.x + point$b.x,
		y: point$a.y + point$b.y
	}
}

export function subtractPoints(
	point$a: Types.Point,
	point$b: Types.Point
): Types.Point {
	return {
		x: point$b.x - point$a.x,
		y: point$b.y - point$a.y
	}
}

function applyConstantRotation(
	point: Types.Point, 
	constants: { cos: number; sin: number }
): Types.Point {
	const { x, y } = point
	const { sin, cos } = constants
	// REVIEW: Reversed because of browser coords? Should it be...
	// {
	//	x: cos * x + sin * y,
	//	y: sin * x - cos * y
	//}
	return asPoint(
		cos * x + sin * y,
		cos * y - sin * x
	)
}

function applyRotation(
	point: Types.Point, 
	radians: number
): Types.Point {
	const constants = { cos: Math.cos(radians), sin: Math.sin(radians) }
	return applyConstantRotation(point, constants)
}

function applyTranslation(
	point: Types.Point, 
	translate: Types.Point
): Types.Point {
	const { x, y } = point
	const { x: tx, y: ty } = translate
	return {
		x: x + tx,
		y: y + ty
	}
}

// function applyScale(point: Types.Point, scale: Types.Point): Types.Point {
// 	const { x, y } = point
// 	const { x: sx, y: sy } = scale
// 	return {
// 		x: x * sx,
// 		y: y * sy
// 	}
// }

function calculateAngleFromVectors(
	vector$a: Types.Vector2D,
	vector$b: Types.Vector2D
): number {
	const [d$a, d$b] = calculateDifferenceOfVectors(vector$a, vector$b)

	return calculateAngleBetweenTwoPoints(d$a, d$b)
}

function calculateAngleBetweenTwoPoints(
	a: Types.Point, 
	b: Types.Point
): number {
	return Math.atan2(
		calculateDotProduct(a, b),
		calculateDeterminant(a, b)
	)
}


export function calculateAreaOfDimension(
	{ width, height }: Types.Dimension
): number {
	return width * height
}


function calculateAreaOfPolygon(points: Types.Point[]): number {
	let total = 0;

	for (let i = 0, l = points.length; i < l; i++) {
		const addX = points[i].x;
		const addY = points[i === points.length - 1 ? 0 : i + 1].y;
		const subX = points[i === points.length - 1 ? 0 : i + 1].x;
		const subY = points[i].y;

		total += (addX * addY * 0.5);
		total -= (subX * subY * 0.5);
	}

	return Math.abs(total);
}

function calculateDotProduct(
	a: Types.Point, 
	b: Types.Point
): number {
	return a.x * b.y - a.y * b.x
}

function calculateDeterminant(
	a: Types.Point, 
	b: Types.Point
): number {
	return a.x * b.x + a.y * b.y
}

function calculateDifferenceOfVectors(
	vector$a: Types.Vector2D,
	vector$b: Types.Vector2D
): Types.Vector2D {
	const d$a = {
		x: vector$a[0].x - vector$a[1].x,
		y: vector$a[0].y - vector$a[1].y
	}

	const d$b = {
		x: vector$b[0].x - vector$b[1].x,
		y: vector$b[0].y - vector$b[1].y
	}

	return [d$a, d$b]
}


function getBoundsFromPoints(
	points: Types.Point[]
): Types.Bounds {
	const [xCoords, yCoords] = getCoordinatesFromPoints(points)
	let top = Infinity
	let right = -Infinity
	let bottom = -Infinity
	let left = Infinity

	for(let i = 0; i < xCoords.length, i < yCoords.length; i++){
		top = Math.min(yCoords[i], top)
		right = Math.max(xCoords[i], right)
		bottom = Math.max(yCoords[i], bottom)
		left = Math.min(xCoords[i], left)
	}

	return { top, right, bottom, left }
}

function getCenterDisplacementFromBounds(
	{ top, right, bottom, left }: Types.Bounds,
): Types.Point {
	return asPoint(
		left + (right - left) / 2,
		top + (bottom - top) / 2
	)
}

function getCenterDisplacementFromPoints(
	points: Types.Point[]
): Types.Point {
	const bounds = getBoundsFromPoints(points)
	return getCenterDisplacementFromBounds(bounds)
}


function getCoordinatesFromPoints(
	points: Types.Point[]
): Types.Pair<number[]> {
	const xCoords: number[] = []
	const yCoords: number[] = []

	let point: Types.Point
	for(let i = 0; i < points.length; i++){
		point = points[i]
		xCoords.push(point.x)
		yCoords.push(point.y)
	}

	return [xCoords, yCoords]
}

function getDimensionsFromVectors(
	a: Types.Vector2D, 
	b: Types.Vector2D
): Types.Dimension {
	const [d$a, d$b] = calculateDifferenceOfVectors(a, b)
	const width = Math.sqrt((d$a.x ** 2) + (d$a.y ** 2))
	const height = Math.sqrt((d$b.x ** 2) + (d$b.y ** 2))
	return asDimension(width, height)
}

// function getDisplacementFromBounds(
// 	{ top, left }: Types.Bounds,
// ): Types.Point {
// 	return asPoint(
// 		left,
// 		top
// 	)
// }

// function getDisplacementFromPoints(
// 	points: Types.Point[]
// ): Types.Point {
// 	const bounds = getBoundsFromPoints(points)
// 	return getDisplacementFromBounds(bounds)
// }

function getFirstVectorPair(
	points: Types.Point[]
): Types.Pair<Types.Vector2D> {
	let [point$a, point$b] = points
	if(!point$b) point$b = point$a
	const point$c = points[points.length - 1]

	const vector$ab = [point$a, point$b] as Types.Vector2D
	const vector$ac = [point$a, point$c] as Types.Vector2D

	return [vector$ab, vector$ac]
}

// TODO: This is incorrect, needs to only do this on rectangular shapes. Other shapes cannot be detected/optimized
// TODO: Also needs to detect if 2 points are on same line and remove
export function getWidthFirstDimensions(
	points: Types.Point[]
): [
	vectors: Types.Pair<Types.Vector2D>, 
	size: Types.Dimension, 
	aspect: number
] {
	let [a, b] = getFirstVectorPair(points)
	let size = getDimensionsFromVectors(a, b)
	let aspect = size.width / size.height

	if(aspect < 1){
		const firstElement = points.shift();
		if(isDefined(firstElement)){
			points.push(firstElement);
			([a, b] = getFirstVectorPair(points));
			size = getDimensionsFromVectors(a, b)
			aspect = size.width / size.height
		}
	}

	return [
		[a, b],
		size,
		aspect
	]
}

// function getVectorPair(
// 	points: Types.Point[], 
// 	index: number
// ): [] | Types.Pair<Types.Vector2D> {
// 	if(points.length === 1){
// 		console.warn('Attemted to get vector pair on polygon with only 1 point.')
// 		return []
// 	}
// 	if(points.length < 2){
// 		console.warn('Attemted to get vector pair on polygon with only 2 point.')
// 		return []
// 	}

// 	const prevIndex = index === 0 ? points.length - 1 : index - 1
// 	const nextIndex = index === points.length - 1 ? 0 : index + 1

// 	const point$a = points[index]
// 	const point$b = points[prevIndex]
// 	const point$c = points[nextIndex]

// 	const vector$ab = [point$a, point$b] as Types.Vector2D
// 	const vector$ac = [point$a, point$c] as Types.Vector2D

// 	return [vector$ab, vector$ac]

// }

function mapPointToPrecisionFactory(
	precision: number = DEFAULT_PRECISION
): (point: Types.Point) => Types.Point {
	return (point: Types.Point): Types.Point => roundPoint(point, precision)
}

function mapStringToPoint(point: string): Types.Point {
	const parts = point.split(',')
	const x = parseFloat(parts[0].trim())
	const y = parseFloat(parts[1].trim())

	return { x, y }
}
function normalizeRotation(angle: number): number {
	while(angle < 0 || angle >= MATH_2PI){
		angle = angle < 0 ? angle + MATH_2PI
			: angle > MATH_2PI ? angle - MATH_2PI
			: angle
	}
	return angle
}

function removeDuplicatePoints(
	points: Types.Point[]
): Types.Point[] {
	if(points.length < 2) return points
	const firstPoint = points[0]
	const lastPoint = points[points.length - 1]

	if(firstPoint.x === lastPoint.x && firstPoint.y === lastPoint.y){
		points.pop()
	}

	return points
}

export function roundBounds(
	{ top, right, bottom, left }: Types.Bounds,
	precision: number = DEFAULT_PRECISION
): Types.Bounds {
	return {
		top: roundToPrecision(top, precision),
		right: roundToPrecision(right, precision),
		bottom: roundToPrecision(bottom, precision),
		left: roundToPrecision(left, precision)
	}
}

function roundPoint(
	{ x, y }: Types.Point,
	precision: number = DEFAULT_PRECISION
): Types.Point {
	return {
		x: roundToPrecision(x, precision),
		y: roundToPrecision(y, precision)
	}
}

function roundPoints(
	points: Types.Point[],
	precision: number = DEFAULT_PRECISION
): Types.Point[] {
	const mapPointToPrecision = mapPointToPrecisionFactory(precision)
	return points.map(mapPointToPrecision)
}

function roundPolygon(
	{ area, count, points, position, rotation, size }: Types.PolygonDetails,
	precision: number = DEFAULT_PRECISION
): Types.PolygonDetails {
	// edges = {
	// 	global: roundEdges(edges.global, precision),
	// 	local: roundEdges(edges.local, precision),
	// 	untransformed: roundEdges(edges.untransformed, precision)
	// }
	points = {
		global: roundPoints(points.global, precision),
		local: roundPoints(points.local, precision),
		untransformed: roundPoints(points.untransformed, precision)
	}
	position = roundPoint(position, precision)
	rotation = roundToPrecision(rotation, precision)
	size = roundDimensions(size, precision)
	area = size.width * size.height

	const bounds: Types.BoundSet = {
		global: getBoundsFromPoints(points.global),
		local: getBoundsFromPoints(points.local),
		untransformed: getBoundsFromPoints(points.untransformed)
	}

	return { area, count, points, position, rotation, size, bounds }
}

export function roundDimensions(
	{ width, height }: Types.Dimension,
	precision: number = DEFAULT_PRECISION
): Types.Dimension {
	return {
		width: roundToPrecision(width, precision),
		height: roundToPrecision(height, precision)
	}
}
// function roundBoundingBox(
// 	{ area, bounds, points, position, size }: Types.BoundingBox,
// 	precision: number = DEFAULT_PRECISION
// ): Types.BoundingBox {
// 	bounds = roundBounds(bounds, precision)
// 	points = roundPoints(points, precision)
// 	position = roundPoint(position, precision)
// 	size = roundDimensions(getDimensionsFromBounds(bounds), precision + 1)
// 	area = roundToPrecision(calculateAreaOfDimension(size), precision + 1)
// 	return {
// 		area,
// 		bounds,
// 		points,
// 		position,
// 		size
// 	}
// }

function sortPointsRadially(
	points: Types.Point[], 
	center: Types.Point
): Types.Point[] {
	let entries: Types.Entry<number, Types.Point>[] = []
	let startingAngle: Types.Optional<number>

	let point: Types.Point
	let angle: number
	for(let i = 0; i < points.length; i++){
		point = points[i]
		if(!point) continue

		angle = Math.atan2(point.x - center.x, point.y - center.y)
		if(isDefined(startingAngle)) {
			if(angle < startingAngle){
				angle += Math.PI * 2
			}
		} else {
			startingAngle = angle
		}
		entries.push([angle, point])
	}

	if(entries.length){
		entries = entries.sort(
			([a], [b]) => a - b
		)

		entries.reverse()
		entries.unshift(entries.pop() as Types.Entry<number, Types.Point>)
	}


	return entries.map(([, p]) => p)
}

function translatePoints(
	points: Types.Point[],
	translation: Types.Point
): Types.Point[] {
	return points.map(
		point => applyTranslation(point, translation)
	)
}


export function asBounds(
	maybeAllTopOrVertical?: Types.Optional<number>, 
	maybeRightOrHorizontal?: Types.Optional<number>, 
	maybeBottom?: Types.Optional<number>, 
	maybeLeft?: Types.Optional<number>
): Types.Bounds {
	let top = 0
	let right = 0
	let bottom = 0
	let left = 0

	if(isNumber(maybeAllTopOrVertical)){
		if(!isNumber(maybeRightOrHorizontal)){
			top = maybeAllTopOrVertical
			right = maybeAllTopOrVertical
			bottom = maybeAllTopOrVertical
			left = maybeAllTopOrVertical
		} else if(!isNumber(maybeBottom)){
			top = maybeAllTopOrVertical
			right = maybeRightOrHorizontal
			bottom = maybeAllTopOrVertical
			left = maybeRightOrHorizontal
		} else if(!isNumber(maybeLeft)){
			top = maybeAllTopOrVertical
			right = maybeRightOrHorizontal
			bottom = maybeBottom
			left = maybeRightOrHorizontal
		} else {
			top = maybeAllTopOrVertical
			right = maybeRightOrHorizontal
			bottom = maybeBottom
			left = maybeLeft
		}
	}

	return { top, right, bottom, left }
}

export function asDimension(
	widthOrBoth?: Types.Optional<number>,
	height?: Types.Optional<number>
): Types.Dimension {
	let width: number

	if(isNumber(widthOrBoth)){
		width = widthOrBoth
		if(!isNumber(height)){
			height = widthOrBoth
		}
	} else {
		width = 0
		height = 0
	}

	return { width, height }
}

export function asPoint(
	x = 0,
	y = 0
): Types.Point {
	return { x, y }
}

export function asTransform(
	[tx, ty]: Types.Pair<number> = [0, 0],
	scale: number | Types.Pair<number> = 1,
	rotate = 0
): Types.Transform {
	return {
		translate: asPoint(tx, ty),
		scale: isArray(scale) ? asPoint(scale[0], scale[1]) : asPoint(scale, scale),
		rotate
	}
}

export function convertBoundsToDimension(bounds: Types.Bounds): Types.Dimension {
	return asDimension(
		bounds.left + bounds.right,
		bounds.top + bounds.bottom
	)
}

export function modifyDimensionByBounds(
	dimension: Types.Dimension, 
	bounds: Types.Bounds
): Types.Dimension {
	return asDimension(
		dimension.width - bounds.left - bounds.right,
		dimension.height - bounds.top - bounds.bottom
	)
}

export function getDimensionsFromBounds(
	bounds: Types.Bounds
): Types.Dimension {
	return {
		width: Math.abs(bounds.right - bounds.left),
		height: Math.abs(bounds.bottom - bounds.top)
	}
}

/**
 * This function converts the 'points' attribute of any SVG Element
 * to a [[Point[]]]
 *
 * @param points A string of points separated by spaces with each
 * coordinate separated by commas.
 *
 * @example
 * ```typescript
 * let pointStr = '0,0 10,5'
 * let points = getPointsFromString(pointstr)
 *
 * // [{ x: 0, y: 0 }, { x: 10, y: 5 }]
 * ```
 */

 export function getPointsFromString(
	pointsStr: Types.Optional<string>, 
	precision?: Types.Optional<number>
	
): Types.Point[] {
	const points: Types.Point[] = []
	if(!isString(pointsStr)) return points

	const parts = pointsStr.trim().split(' ')

	let pointStr: string
	let point: Types.Point
	for(let i = 0; i < parts.length; i++){
		pointStr = parts[i]
		if(!isDefined(pointStr)) continue
		point = mapStringToPoint(pointStr)
		if(isDefined(precision)) point = roundPoint(point, precision)
		points.push(point)
	}

	return points
}


export function createPolygonFromPoints(
	global: Types.Point[],
	precision?: Types.Optional<number>
): Types.PolygonDetails {
	global = removeDuplicatePoints(global)
	const position = getCenterDisplacementFromPoints(global)

	global = sortPointsRadially(global, position)
	const local = translatePoints(global, position)

	const [[a], size] = getWidthFirstDimensions(local)

	// BUG: Rotation does not get calculated correctly on non-rectangular polygons
	// TODO: Fix rotation detection, does not work on some polygons
	let rotation = calculateAngleFromVectors(a, UNIT_X)
	// let reverseRotation = calculateAngleFromVectors(b, x)

	// rotation = 0
	rotation = normalizeRotation(rotation)
	if(rotation > Math.PI) rotation -= Math.PI
	// reverseRotation = normalizeRotation(reverseRotation)

	const area = calculateAreaOfPolygon(local)
	const count = local.length

	const untransformed: Types.Point[] = []
	let p: Types.Point
	for(let i = 0; i < local.length; i++){
		p = local[i]
		if(!p) continue

		untransformed.push(applyRotation(p, -rotation))
	}

	let polygon: Types.PolygonDetails = {
		area,
		count,
		position,
		rotation,
		size,
		bounds: {
			global: getBoundsFromPoints(global),
			local: getBoundsFromPoints(local),
			untransformed: getBoundsFromPoints(untransformed)
		},
		points: {
			global,
			local,
			untransformed
		},
	}

	if(isDefined(precision)){
		polygon = roundPolygon(polygon, precision)
	}

	return polygon
}

export function displayPointsAsString(
	points: Types.Point[], 
	precision: Types.Maybe<number> = false
): string {
	let displayPoints = ''
	let point: Types.Point
	let str: string
	for(let i = 0; i < points.length; i++){
		point = points[i]
		if(!point) continue

		str = displayPointAsString(point, precision)
		if(displayPoints.length){
			displayPoints += ' ' + str
		} else {
			displayPoints += str
		}

	}
	return displayPoints
}

export function displayPointAsString(
	{ x, y }: Types.Point, 
	precision: Types.Maybe<number> = false
): string {
	if(!precision) return x + ',' + y
	return roundToPrecision(x, precision) + ',' + roundToPrecision(y, precision)
}

/*
 * Convex hull algorithm - Library (TypeScript)
 *
 * Copyright (c) 2018 Project Nayuki
 * https://www.nayuki.io/page/convex-hull-algorithm
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * along with this program (see COPYING.txt and COPYING.LESSER.txt).
 * If not, see <http://www.gnu.org/licenses/>.
 */

// Returns a new array of points representing the convex hull of
// the given set of points. The convex hull excludes collinear points.
// This algorithm runs in O(n log n) time.
export function convexHull(points: Types.Point[]): Types.Point[] {
	const newPoints = points.slice();
	newPoints.sort(comparePoints);
	return makeHullPresorted(newPoints);
}

function comparePoints(a: Types.Point, b: Types.Point): number {
	if (a.x < b.x)
		return -1;
	else if (a.x > b.x)
		return +1;
	else if (a.y < b.y)
		return -1;
	else if (a.y > b.y)
		return +1;
	else
		return 0;
}


// Returns the convex hull, assuming that each points[i] <= points[i + 1]. Runs in O(n) time.
function makeHullPresorted(points: Types.Point[]): Types.Point[] {
	if (points.length <= 1)
		return points.slice();

	// Andrew's monotone chain algorithm. Positive y coordinates correspond to "up"
	// as per the mathematical convention, instead of "down" as per the computer
	// graphics convention. This doesn't affect the correctness of the result.

	const upperHull: Types.Point[] = [];
	for (let i = 0; i < points.length; i++) {
		const p: Types.Point = points[i];
		while (upperHull.length >= 2) {
			const q: Types.Point = upperHull[upperHull.length - 1];
			const r: Types.Point = upperHull[upperHull.length - 2];
			if ((q.x - r.x) * (p.y - r.y) >= (q.y - r.y) * (p.x - r.x))
				upperHull.pop();
			else
				break;
		}
		upperHull.push(p);
	}
	upperHull.pop();

	const lowerHull: Types.Point[] = [];
	for (let i = points.length - 1; i >= 0; i--) {
		const p: Types.Point = points[i];
		while (lowerHull.length >= 2) {
			const q: Types.Point = lowerHull[lowerHull.length - 1];
			const r: Types.Point = lowerHull[lowerHull.length - 2];
			if ((q.x - r.x) * (p.y - r.y) >= (q.y - r.y) * (p.x - r.x))
				lowerHull.pop();
			else
				break;
		}
		lowerHull.push(p);
	}
	lowerHull.pop();

	if (upperHull.length === 1 && lowerHull.length === 1 && upperHull[0].x === lowerHull[0].x && upperHull[0].y === lowerHull[0].y)
		return upperHull
	else
		return upperHull.concat(lowerHull)
}